// src/pages/Kyc.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { submitKyc, getKycStatus } from "../redux/slices/kycSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Shield,
  Upload,
  Camera,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  CreditCard,
  Car,
  Sparkles,
  User,
  FileCheck,
} from "lucide-react";
import toast from "react-hot-toast";

// Type for document types
type DocumentType = "passport" | "id_card" | "driver_license";

const Kyc: React.FC = () => {
  const dispatch = useDispatch();
  const { kyc, isLoading } = useSelector((state: RootState) => state.kyc);
  const { user } = useSelector((state: RootState) => state.auth);
  const [documentType, setDocumentType] = useState<DocumentType>("passport");
  const [documentFront, setDocumentFront] = useState<File | null>(null);
  const [documentBack, setDocumentBack] = useState<File | null>(null);
  const [selfiePhoto, setSelfiePhoto] = useState<File | null>(null);
  const [previewFront, setPreviewFront] = useState<string>("");
  const [previewBack, setPreviewBack] = useState<string>("");
  const [previewSelfie, setPreviewSelfie] = useState<string>("");

  useEffect(() => {
    dispatch(getKycStatus() as any);
  }, [dispatch]);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "front" | "back" | "selfie",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === "front") {
        setDocumentFront(file);
        setPreviewFront(reader.result as string);
      } else if (type === "back") {
        setDocumentBack(file);
        setPreviewBack(reader.result as string);
      } else {
        setSelfiePhoto(file);
        setPreviewSelfie(reader.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!documentFront || !selfiePhoto) {
      toast.error("Please upload all required documents");
      return;
    }

    // Check if document back is required (for ID card and driver license)
    const requiresBack =
      documentType === "id_card" || documentType === "driver_license";
    if (requiresBack && !documentBack) {
      toast.error("Please upload both sides of your document");
      return;
    }

    const formData = new FormData();
    formData.append("document_type", documentType);
    formData.append("document_front", documentFront);
    if (documentBack) formData.append("document_back", documentBack);
    formData.append("selfie_photo", selfiePhoto);

    try {
      await dispatch(submitKyc(formData) as any);
      toast.success("KYC submitted successfully!");

      // Reset form
      setDocumentFront(null);
      setDocumentBack(null);
      setSelfiePhoto(null);
      setPreviewFront("");
      setPreviewBack("");
      setPreviewSelfie("");
      setDocumentType("passport");
    } catch (error) {
      toast.error("Failed to submit KYC");
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "rejected":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "pending":
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    if (!status) return "Unknown";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getDocumentTypeIcon = (type: DocumentType) => {
    switch (type) {
      case "passport":
        return <FileText className="h-5 w-5" />;
      case "id_card":
        return <CreditCard className="h-5 w-5" />;
      case "driver_license":
        return <Car className="h-5 w-5" />;
    }
  };

  const requiresBackDocument =
    documentType === "id_card" || documentType === "driver_license";

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
            <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 p-12">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin mx-auto"></div>
                <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
              </div>
              <p className="mt-6 text-gray-600 dark:text-gray-400 font-medium">
                Loading KYC status...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      <div className="p-4 lg:p-5">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              KYC Verification
            </h1>
            <Sparkles className="h-4 w-4 text-primary/60" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">
            Complete your identity verification to access all platform features
          </p>
        </div>

        {/* KYC Status */}
        {kyc && kyc.status && (
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-6 shadow-lg mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                {getStatusIcon(kyc.status)}
                <div className="ml-3">
                  <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                    KYC Status: {getStatusText(kyc.status)}
                  </h3>
                  {kyc.submitted_at && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Submitted on{" "}
                      {new Date(kyc.submitted_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <span
                className={`${GlobalStyles.badge} ${
                  kyc.status === "approved"
                    ? GlobalStyles.badgeSuccess
                    : kyc.status === "pending"
                      ? GlobalStyles.badgeWarning
                      : kyc.status === "rejected"
                        ? GlobalStyles.badgeError
                        : ""
                }`}
              >
                {kyc.status}
              </span>
            </div>

            {kyc.admin_notes && (
              <div
                className={`${GlobalStyles.alert} ${
                  kyc.status === "rejected"
                    ? GlobalStyles.alertError
                    : GlobalStyles.alertInfo
                } backdrop-blur-sm`}
              >
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">
                    {kyc.status === "rejected"
                      ? "Reason for rejection:"
                      : "Admin notes:"}
                  </p>
                  <p className="text-sm mt-1">{kyc.admin_notes}</p>
                </div>
              </div>
            )}

            {kyc.status === "pending" && (
              <div
                className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} mt-4 backdrop-blur-sm`}
              >
                <Clock className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm">
                  Your KYC verification is under review. This process usually
                  takes 24-48 hours.
                </p>
              </div>
            )}
          </div>
        )}

        {/* KYC Form - Show if no KYC or if KYC was rejected */}
        {(!kyc || !kyc.status || kyc.status === "rejected") && (
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-6 shadow-lg">
            <div className="text-center mb-8">
              <div className="mx-auto w-20 h-20 bg-gray-100/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-2">
                Identity Verification
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Please provide the following documents for verification
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Document Type */}
              <div>
                <h4 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-4">
                  Select Document Type
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(
                    ["passport", "id_card", "driver_license"] as DocumentType[]
                  ).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setDocumentType(type)}
                      className={`p-5 rounded-xl border flex flex-col items-center justify-center transition-all duration-200 group ${
                        documentType === type
                          ? "border-primary bg-primary/10 dark:bg-primary/20 shadow-sm"
                          : "border-gray-200/50 dark:border-gray-700/50 hover:border-primary/50 hover:bg-primary/5 dark:hover:bg-primary/10"
                      }`}
                    >
                      <div
                        className={`p-3 rounded-lg mb-3 transition-all duration-300 group-hover:scale-110 ${
                          documentType === type
                            ? "bg-primary/20 dark:bg-primary/30"
                            : "bg-gray-100/50 dark:bg-gray-800/50"
                        }`}
                      >
                        {getDocumentTypeIcon(type)}
                      </div>
                      <span className="font-medium capitalize text-gray-900 dark:text-white">
                        {type.replace("_", " ")}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Document Front */}
              <div>
                <h4 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-4">
                  Document Front
                </h4>
                <div className="border-2 border-dashed border-gray-200/50 dark:border-gray-700/50 rounded-xl p-8 hover:border-primary/50 transition-all duration-300 bg-gradient-to-br from-gray-50/50 to-gray-100/30 dark:from-gray-800/30 dark:to-gray-900/20 backdrop-blur-sm">
                  {previewFront ? (
                    <div className="relative">
                      <img
                        src={previewFront}
                        alt="Document Front"
                        className="w-full h-auto rounded-lg max-h-64 object-contain shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setDocumentFront(null);
                          setPreviewFront("");
                        }}
                        className="absolute top-2 right-2 p-1.5 bg-red-500/90 hover:bg-red-600 text-white rounded-full transition-colors backdrop-blur-sm"
                      >
                        <XCircle className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center cursor-pointer group/upload">
                      <div className="p-4 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 mb-4 group-hover/upload:scale-110 transition-transform duration-300">
                        <Upload className="h-8 w-8 text-primary" />
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 mb-2 text-center">
                        Upload clear photo of document front
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        JPG, PNG (Max 5MB)
                      </p>
                      <div
                        className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} group-hover/upload:border-primary/50`}
                      >
                        <Upload className="h-4 w-4 mr-2 transition-transform group-hover/upload:-translate-y-0.5" />
                        Select File
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, "front")}
                          required
                        />
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {/* Document Back (only for ID card and driver license) */}
              {requiresBackDocument && (
                <div>
                  <h4 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-4">
                    Document Back
                  </h4>
                  <div className="border-2 border-dashed border-gray-200/50 dark:border-gray-700/50 rounded-xl p-8 hover:border-primary/50 transition-all duration-300 bg-gradient-to-br from-gray-50/50 to-gray-100/30 dark:from-gray-800/30 dark:to-gray-900/20 backdrop-blur-sm">
                    {previewBack ? (
                      <div className="relative">
                        <img
                          src={previewBack}
                          alt="Document Back"
                          className="w-full h-auto rounded-lg max-h-64 object-contain shadow-sm"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setDocumentBack(null);
                            setPreviewBack("");
                          }}
                          className="absolute top-2 right-2 p-1.5 bg-red-500/90 hover:bg-red-600 text-white rounded-full transition-colors backdrop-blur-sm"
                        >
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center cursor-pointer group/upload">
                        <div className="p-4 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 mb-4 group-hover/upload:scale-110 transition-transform duration-300">
                          <Upload className="h-8 w-8 text-primary" />
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mb-2 text-center">
                          Upload clear photo of document back
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                          JPG, PNG (Max 5MB)
                        </p>
                        <div
                          className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} group-hover/upload:border-primary/50`}
                        >
                          <Upload className="h-4 w-4 mr-2 transition-transform group-hover/upload:-translate-y-0.5" />
                          Select File
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileChange(e, "back")}
                            required={requiresBackDocument}
                          />
                        </div>
                      </label>
                    )}
                  </div>
                </div>
              )}

              {/* Selfie Photo */}
              <div>
                <h4 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-4">
                  Selfie with Document
                </h4>
                <div className="border-2 border-dashed border-gray-200/50 dark:border-gray-700/50 rounded-xl p-8 hover:border-primary/50 transition-all duration-300 bg-gradient-to-br from-gray-50/50 to-gray-100/30 dark:from-gray-800/30 dark:to-gray-900/20 backdrop-blur-sm">
                  {previewSelfie ? (
                    <div className="relative">
                      <img
                        src={previewSelfie}
                        alt="Selfie"
                        className="w-full h-auto rounded-lg max-h-64 object-contain shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setSelfiePhoto(null);
                          setPreviewSelfie("");
                        }}
                        className="absolute top-2 right-2 p-1.5 bg-red-500/90 hover:bg-red-600 text-white rounded-full transition-colors backdrop-blur-sm"
                      >
                        <XCircle className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center cursor-pointer group/upload">
                      <div className="p-4 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 mb-4 group-hover/upload:scale-110 transition-transform duration-300">
                        <Camera className="h-8 w-8 text-primary" />
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 mb-2 text-center">
                        Take a selfie while holding your document
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Face and document must be clearly visible
                      </p>
                      <div
                        className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} group-hover/upload:border-primary/50`}
                      >
                        <Camera className="h-4 w-4 mr-2 transition-transform group-hover/upload:-translate-y-0.5" />
                        Take Photo
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, "selfie")}
                          capture="environment"
                          required
                        />
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {/* Requirements */}
              <div
                className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} backdrop-blur-sm`}
              >
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">
                    Verification Requirements:
                  </p>
                  <ul className="text-sm mt-2 space-y-1">
                    <li>• All documents must be valid and not expired</li>
                    <li>• Photos must be clear and all text readable</li>
                    <li>
                      • Selfie must clearly show your face and the document
                    </li>
                    <li>• All information must match your account details</li>
                  </ul>
                </div>
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} w-full group/submit`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-5 h-5 mr-2 border-2`}
                      ></div>
                      Submitting...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <Shield className="h-5 w-5 mr-2 transition-transform group-hover/submit:-translate-x-0.5" />
                      Submit for Verification
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* KYC Benefits - Show if user needs to complete KYC */}
        {user?.verification_level === 1 &&
          (!kyc || !kyc.status || kyc.status === "rejected") && (
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-6 shadow-lg mt-8">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6">
                Benefits of KYC Verification
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-5 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-100/50 to-green-50/50 dark:from-green-900/20 dark:to-green-800/10 flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                    <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Higher Limits
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Increased deposit and withdrawal limits
                  </p>
                </div>
                <div className="p-5 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-100/50 to-blue-50/50 dark:from-blue-900/20 dark:to-blue-800/10 flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                    <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Enhanced Security
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Added protection for your account
                  </p>
                </div>
                <div className="p-5 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-100/50 to-purple-50/50 dark:from-purple-900/20 dark:to-purple-800/10 flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                    <User className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Full Access
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Access to all platform features including recovery services
                  </p>
                </div>
              </div>
            </div>
          )}

        {/* Verification Levels Info */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-6 shadow-lg mt-8">
          <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6">
            Verification Levels
          </h3>
          <div className="space-y-4">
            <div className="flex items-center p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
              <div className="p-2 rounded-lg bg-yellow-500/10 mr-4">
                <FileCheck className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Level 1: Email Verified
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Basic account access with limited features
                </p>
              </div>
            </div>
            <div className="flex items-center p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
              <div className="p-2 rounded-lg bg-blue-500/10 mr-4">
                <FileText className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Level 2: KYC Submitted
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Waiting for verification approval
                </p>
              </div>
            </div>
            <div className="flex items-center p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
              <div className="p-2 rounded-lg bg-green-500/10 mr-4">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Level 3: KYC Approved
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Full access to all platform features and services
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Kyc;
